import { Fragment } from 'react'
import FaturaItem from './FaturaItem'
import {
    formatDetalheCategoria,
    formatDetalheValor,
    groupDetalhesAtendimento,
    isDetalheNegativo,
} from '@/services/fatura'
import type { DetalheFatura, Fatura } from '@/types/fatura'
import { formatDate, formatMoney } from '@/utils'
import './FaturaItem.scss'
import './FaturasView.scss'

interface FaturasPagasCardProps {
    faturas: Fatura[]
}

function toDate(value?: string) {
    if (!value) return null
    const parsed = new Date(value.replace(/-/g, '/'))
    return Number.isNaN(parsed.getTime()) ? null : parsed
}

function formatInlineDate(value?: string) {
    const parsed = toDate(value)
    if (!parsed) return ''
    return parsed.toLocaleDateString('pt-BR')
}

function AtendimentoHeaderRow({
    codigo,
    pagamento,
    categoria,
    titulo,
    valor,
    hasDetails,
}: {
    codigo?: number | null
    pagamento?: string
    categoria: string
    titulo: string
    valor: string
    hasDetails: boolean
}) {
    return (
        <tr className={`fatura-aberta-row-header is-paid${hasDetails ? ' has-details' : ''}`}>
            <td className="fatura-col-id">
                {codigo ? <span className="fatura-aberta-id">#{codigo}</span> : null}
                {pagamento ? <div className="fatura-aberta-venc">{formatDate(pagamento)}</div> : null}
                <small className="fatura-aberta-pago-status">Pago</small>
            </td>
            <td>
                {categoria ? <div className="fatura-aberta-category">{categoria}</div> : null}
                <div className="fatura-aberta-title">{titulo}</div>
            </td>
            <td className="text-right">
                {valor ? <strong className="fatura-aberta-valor">{valor}</strong> : null}
            </td>
        </tr>
    )
}

function DetalheServicoRow({
    item,
    isFirst,
    isLast,
}: {
    item: DetalheFatura
    isFirst?: boolean
    isLast?: boolean
}) {
    const dateText = formatInlineDate(item.Data)
    const valorExibido = formatDetalheValor(item, formatMoney)
    const negativo = isDetalheNegativo(item)
    const isPlano = Boolean(item.Plano)

    return (
        <tr
            className={[
                'fatura-detalhe-row',
                isFirst ? 'is-first' : '',
                isLast ? 'is-last' : '',
                negativo ? 'is-negative' : '',
            ].filter(Boolean).join(' ')}
        >
            <td className="fatura-col-id" aria-hidden="true" />
            <td>
                <div className="fatura-detalhe-line">
                    {dateText ? <span className="fatura-detalhe-date">{dateText}</span> : null}
                    <span className="fatura-detalhe-desc">{item.Descricao}</span>
                </div>
            </td>
            <td
                className={[
                    'text-right',
                    'fatura-detalhe-valor',
                    negativo ? 'is-negative' : '',
                    isPlano ? 'is-plano' : '',
                ].filter(Boolean).join(' ')}
            >
                {valorExibido}
            </td>
        </tr>
    )
}

function OutroDetalheRow({ item, data }: { item: DetalheFatura; data?: string }) {
    const dateText = formatInlineDate(item.Data ?? data)
    const itemCode = item.Index ?? item.CdClientePlano
    const valorExibido = formatDetalheValor(item, formatMoney)
    const negativo = isDetalheNegativo(item)
    const isPlano = Boolean(item.Plano)

    return (
        <tr className={`fatura-detalhe-row${negativo ? ' is-negative' : ''}`}>
            <td className="fatura-col-id">
                {itemCode ? <span className="fatura-aberta-id">#{itemCode}</span> : null}
            </td>
            <td>
                {dateText ? <div className="fatura-detalhe-date">{dateText}</div> : null}
                <div className="fatura-detalhe-desc">{item.Descricao}</div>
                {item.Animais ? <div className="fatura-detalhe-animais">{item.Animais}</div> : null}
            </td>
            <td
                className={[
                    'text-right',
                    'fatura-detalhe-valor',
                    negativo ? 'is-negative' : '',
                    isPlano ? 'is-plano' : '',
                ].filter(Boolean).join(' ')}
            >
                {valorExibido}
            </td>
        </tr>
    )
}

export default function FaturasPagasCard({ faturas }: FaturasPagasCardProps) {
    if (faturas.length === 0) return null

    return (
        <div className="faturas-aberta-card">
            <table className="fatura-aberta-table faturas-desktop-only">
                <thead>
                    <tr>
                        <th style={{ width: 128 }}>Código</th>
                        <th>Descrição</th>
                        <th className="text-right" style={{ width: 180 }}>Valor</th>
                    </tr>
                </thead>
                <tbody>
                    {faturas.map((fatura, index) => {
                        const detalhes = fatura.Detalhes ?? []
                        const { grupos, outros } = groupDetalhesAtendimento(detalhes)

                        return (
                            <Fragment key={fatura.CdFatura}>
                                {index > 0 && (
                                    <tr className="fatura-group-spacer" aria-hidden="true">
                                        <td colSpan={3} />
                                    </tr>
                                )}

                                {grupos.map((grupo, grupoIndex) => {
                                    const usoDate = formatInlineDate(grupo.principal.Data)
                                    const categoria = formatDetalheCategoria(grupo.principal, usoDate)
                                    const titulo =
                                        grupo.principal.Tipo === 2
                                            ? (grupo.principal.Frequencia || grupo.principal.Descricao || fatura.Titulo)
                                            : (grupo.principal.Descricao || fatura.Titulo)
                                    const mostrarValorFatura = grupoIndex === 0
                                    const itensDetalhe =
                                        grupo.principal.Tipo === 2
                                            ? grupo.itens.filter(item => !item.Principal)
                                            : grupo.itens

                                    return (
                                        <Fragment key={`${fatura.CdFatura}-${grupo.index}`}>
                                            <AtendimentoHeaderRow
                                                codigo={grupo.index}
                                                pagamento={grupoIndex === 0 ? fatura.DataPagamento ?? undefined : undefined}
                                                categoria={categoria}
                                                titulo={titulo || ''}
                                                valor={mostrarValorFatura ? formatMoney(fatura.Valor) : ''}
                                                hasDetails={itensDetalhe.length > 0}
                                            />
                                            {itensDetalhe.map((item, detalheIndex) => (
                                                <DetalheServicoRow
                                                    key={`${fatura.CdFatura}-${grupo.index}-${detalheIndex}`}
                                                    item={item}
                                                    isFirst={detalheIndex === 0}
                                                    isLast={detalheIndex === itensDetalhe.length - 1}
                                                />
                                            ))}
                                        </Fragment>
                                    )
                                })}

                                {grupos.length === 0 && (
                                    <AtendimentoHeaderRow
                                        codigo={outros[0]?.CdClientePlano ?? outros[0]?.Index}
                                        pagamento={fatura.DataPagamento ?? undefined}
                                        categoria=""
                                        titulo={fatura.Titulo}
                                        valor={formatMoney(fatura.Valor)}
                                        hasDetails={outros.length > 0}
                                    />
                                )}

                                {outros.map((item, detalheIndex) => (
                                    <OutroDetalheRow
                                        key={`${fatura.CdFatura}-outro-${detalheIndex}`}
                                        item={item}
                                        data={fatura.DataPagamento ?? fatura.DataVencimento}
                                    />
                                ))}
                            </Fragment>
                        )
                    })}
                </tbody>
            </table>

            <div className="faturas-mobile-only fatura-item-list">
                {faturas.map(fatura => (
                    <FaturaItem key={fatura.CdFatura} fatura={fatura} />
                ))}
            </div>
        </div>
    )
}
